import { useState } from 'react';
import { submitLead } from '../../services/leadService';

const ENQUIRY_OPTIONS = [
  { label: 'Job related', value: 'job-related' },
  { label: 'Service related', value: 'service-related' },
  { label: 'Investment related', value: 'investment-related' },
  { label: 'Others', value: 'others' },
];

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  enquiryType: '',
  message: '',
  website: '',
};

function validateForm(values) {
  const errors = {};

  if (!values.name.trim()) {
    errors.name = 'Name is required.';
  }

  if (!values.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Enter a valid email.';
  }

  if (!values.enquiryType) {
    errors.enquiryType = 'Enquiry type is required.';
  }

  if (values.message.trim().length < 5) {
    errors.message = 'Message must be at least 5 characters.';
  }

  return errors;
}

function ContactForm() {
  const [values, setValues] = useState(initialFormState);
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });

  const updateValue = (event) => {
    const { name, value } = event.target;

    setValues((current) => ({
      ...current,
      [name]: value,
    }));
    setFieldErrors((current) => ({
      ...current,
      [name]: '',
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm(values);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitState({ status: 'error', message: 'Something went wrong. Please try again.' });
      return;
    }

    setSubmitState({ status: 'loading', message: '' });

    try {
      const result = await submitLead({
        ...values,
        formStartedAt,
        source: 'factorstreet-contact',
      });

      setValues(initialFormState);
      setFormStartedAt(Date.now());
      setSubmitState({
        status: 'success',
        message:
          result.message || 'Your enquiry has been received. We will get back to you soon.',
      });
    } catch (error) {
      setSubmitState({
        status: 'error',
        message: 'Something went wrong. Please try again.',
      });
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label>
          <span>Leave this field blank</span>
          <input
            autoComplete="off"
            name="website"
            onChange={updateValue}
            tabIndex={-1}
            type="text"
            value={values.website}
          />
        </label>
      </div>
      <div className="form-grid">
        <label>
          <span>Name</span>
          <input
            autoComplete="name"
            maxLength={120}
            name="name"
            onChange={updateValue}
            placeholder="Your name"
            required
            type="text"
            value={values.name}
          />
          {fieldErrors.name ? <small>{fieldErrors.name}</small> : null}
        </label>

        <label>
          <span>Email</span>
          <input
            autoComplete="email"
            maxLength={160}
            name="email"
            onChange={updateValue}
            placeholder="you@example.com"
            required
            type="email"
            value={values.email}
          />
          {fieldErrors.email ? <small>{fieldErrors.email}</small> : null}
        </label>

        <label>
          <span>Phone</span>
          <input
            autoComplete="tel"
            maxLength={40}
            name="phone"
            onChange={updateValue}
            placeholder="Optional"
            type="tel"
            value={values.phone}
          />
        </label>

        <label>
          <span>Enquiry type</span>
          <select name="enquiryType" onChange={updateValue} required value={values.enquiryType}>
            <option value="">Select an option</option>
            {ENQUIRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {fieldErrors.enquiryType ? <small>{fieldErrors.enquiryType}</small> : null}
        </label>
      </div>

      <label>
        <span>Message</span>
        <textarea
          maxLength={5000}
          name="message"
          onChange={updateValue}
          placeholder="Tell us what you want to discuss."
          required
          rows={7}
          value={values.message}
        />
        {fieldErrors.message ? <small>{fieldErrors.message}</small> : null}
      </label>

      <button className="btn btn--primary contact-form__button" disabled={submitState.status === 'loading'} type="submit">
        {submitState.status === 'loading' ? 'Sending...' : 'Send Message'}
      </button>

      {submitState.message ? (
        <p className={`form-alert form-alert--${submitState.status}`}>{submitState.message}</p>
      ) : null}
    </form>
  );
}

export default ContactForm;
